/**
 * useDiscoveredArtifacts Hooks
 * Phase 1: YouTube + Books Metadata Discovery Pipeline
 * Manage discovered_training_artifacts - approve/reject workflow.
 * 
 * IMPORTANT: Metadata only - no transcripts, no scraped content.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// DTC-L: route expires_at query bound through clock.
import { clock } from '@/utils/clock';

export interface DiscoveredArtifact {
  id: string;
  source_id: string;
  external_id: string;
  title: string;
  artifact_type: string;
  canonical_url: string;
  thumbnail_url: string | null;
  duration_minutes: number | null;
  publish_date: string | null;
  inferred_domains: unknown | null;
  inferred_skill_depth: string | null;
  classification_status: string | null;
  classified_at: string | null;
  original_summary_short: string | null;
  is_paid: boolean;
  price_text: string | null;
  is_promoted: boolean;
  is_rejected: boolean;
  discovery_trigger: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  // ISBN fields for book matching
  isbn_13: string | null;
  isbn_10: string | null;
  source?: {
    id: string;
    source_name: string;
    provider_id: string;
    provider?: {
      id: string;
      display_name: string;
    } | null;
  } | null;
}

// Book-specific approval overrides
export interface BookApprovalOverrides {
  purchase_url?: string;
  purchase_provider_name?: string;
  is_reference_only?: boolean;
}

interface PendingDiscoveriesFilters {
  classificationStatus?: 'pending' | 'classified' | 'failed';
  providerId?: string;
  limit?: number;
}

/**
 * Fetch pending discoveries (not promoted, not rejected)
 * Shows only metadata fields - no transcript or scraped content
 */
export function usePendingDiscoveries(filters?: PendingDiscoveriesFilters) {
  return useQuery({
    queryKey: ['discovered-artifacts', 'pending', filters],
    queryFn: async () => {
      let query = supabase
        .from('discovered_training_artifacts')
        .select(`
          *,
          source:training_sources(
            id,
            source_name,
            provider_id,
            provider:training_providers(id, display_name)
          )
        `)
        .eq('is_promoted', false)
        .eq('is_rejected', false)
        .gt('expires_at', clock.now().toISOString())
        .order('created_at', { ascending: false });

      if (filters?.classificationStatus) {
        query = query.eq('classification_status', filters.classificationStatus);
      }

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;
      if (error) throw error;

      // If filtering by provider, do it client-side after join
      let results = (data || []) as DiscoveredArtifact[];
      if (filters?.providerId) {
        results = results.filter(
          d => (d.source as any)?.provider?.id === filters.providerId
        );
      }

      return results;
    },
  });
}

/**
 * Approve (promote) a discovered artifact to the training catalog
 * Calls the promote-discovered-artifact edge function
 */
export function useApproveDiscovery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      discoveredArtifactId,
      overrides,
    }: {
      discoveredArtifactId: string;
      overrides?: {
        title?: string;
        skill_depth?: string;
        domains?: string[];
        // Book-specific overrides
        purchase_url?: string;
        purchase_provider_name?: string;
        is_reference_only?: boolean;
        isbn_13?: string;
        isbn_10?: string;
      };
    }) => {
      const { data, error } = await supabase.functions.invoke(
        'promote-discovered-artifact?action=promote',
        {
          body: {
            artifact_id: discoveredArtifactId,
            overrides,
          },
        }
      );

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovered-artifacts'] });
      queryClient.invalidateQueries({ queryKey: ['training-artifacts'] });
    },
  });
}

/**
 * Reject a discovered artifact
 * Sets is_rejected = true with optional reason
 */
export function useRejectDiscovery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      discoveredArtifactId,
      reason,
    }: {
      discoveredArtifactId: string;
      reason?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke(
        'promote-discovered-artifact?action=reject',
        {
          body: {
            artifact_id: discoveredArtifactId,
            reason: reason,
          },
        }
      );

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovered-artifacts'] });
    },
  });
}

/**
 * Fetch discovery stats by provider
 */
export function useDiscoveryStats() {
  return useQuery({
    queryKey: ['discovered-artifacts', 'stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('discovered_training_artifacts')
        .select('id, is_promoted, is_rejected, classification_status');

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        pending: data?.filter(d => !d.is_promoted && !d.is_rejected).length || 0,
        promoted: data?.filter(d => d.is_promoted).length || 0,
        rejected: data?.filter(d => d.is_rejected).length || 0,
        classified: data?.filter(d => d.classification_status === 'classified').length || 0,
        pendingClassification: data?.filter(d => d.classification_status === 'pending').length || 0,
      };

      return stats;
    },
  });
}

/**
 * Bulk approve multiple discovered artifacts
 * Skips books (require individual approval with purchase URL)
 */
export function useBulkApproveDiscoveries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      artifactIds,
      onProgress,
    }: {
      artifactIds: string[];
      onProgress?: (completed: number, total: number) => void;
    }) => {
      let approved = 0;
      let failed = 0;
      const errors: string[] = [];

      for (let i = 0; i < artifactIds.length; i++) {
        try {
          const { data, error } = await supabase.functions.invoke(
            'promote-discovered-artifact?action=promote',
            { body: { artifact_id: artifactIds[i] } }
          );
          if (error || data?.error) {
            failed++;
            errors.push(data?.error || error?.message);
          } else {
            approved++;
          }
        } catch (err: any) {
          failed++;
          errors.push(err.message);
        }
        onProgress?.(i + 1, artifactIds.length);
      }

      return { approved, failed, errors };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovered-artifacts'] });
      queryClient.invalidateQueries({ queryKey: ['training-artifacts'] });
    },
  });
}
